import { Injectable } from '@jack-henry/frontend-utils/di';
import {
    AccountReconClient,
    AchClient,
    AchCompaniesClient,
    AchCompanyModelDto,
    AchOffsetAccountsModelDto,
    ApplicationNameTypeStopPaymentServiceDto,
    CompaniesClient,
    CompanyClient,
    ErrorHttpContentDto,
    PositivePayCompanyConfigurationClient,
    ProductFeatureAccountParticipationsResponseDto,
    ProductFeatureModelDto,
    RemoteDepositProductModelDto,
    SaveCompanyStopPaymentRequestDto,
    StopPaymentClient,
    WireCompaniesClient,
    WiresClient,
} from '@treasury/api/bo';
import { HttpResponse } from '@treasury/api/bo/generated/http-client';
import { AchCompany } from '@treasury/domain/company/entities/ach-company.entity';
import { PositivePayType } from '../../types/positive-pay/positive-pay.types';
import { WireCompanyAccount } from '../wires/entities/wire-company-account.entity';
import { WireCompany } from '../wires/entities/wire-company.entity';
import * as ProductFeatures from './entities';
import { AchOffsetAccount } from './entities';
import { CompanyAccount } from './entities/company-account.entity';
import { ProductFeatureAccountParticipation } from './entities/product-features/product-feature-account-participation.entity';
import { mapProductFeatureDto } from './mappers/map-product-feature';
import { FeatureMappingType } from './types';
import { FeatureApiType } from './types/product-feature.enum';

@Injectable()
export class TransferTableService {
    constructor(
        private readonly companiesClient: CompaniesClient,
        private readonly companyClient: CompanyClient,
        private readonly stopPaymentClient: StopPaymentClient,
        private readonly accountReconClient: AccountReconClient,
        private readonly achCompaniesClient: AchCompaniesClient,
        private readonly achClient: AchClient,
        private readonly positivePayCompanyConfigurationClient: PositivePayCompanyConfigurationClient,
        private readonly wireCompaniesClient: WireCompaniesClient,
        private readonly wiresClient: WiresClient
    ) {}

    public async saveWireCompanyAccounts(featureId: number, wireCompany: WireCompany) {
        return await this.wiresClient.wiresCompaniesPost(featureId, wireCompany.toDto());
    }

    public async getWireCompanyAccounts(
        _coreCompanyId: number,
        parentCompanyId: number
    ): Promise<WireCompanyAccount[]> {
        const coreCompanyId = `${_coreCompanyId}`;
        return (
            (
                await this.wireCompaniesClient.wireCompaniesAccounts({
                    coreCompanyId,
                    parentCompanyId,
                    isDli: false,
                })
            ).data.accounts ?? []
        )?.map(account => new WireCompanyAccount(account));
    }

    public async getAddWireCompany(
        coreCompanyId: number,
        parentCompanyId: number
    ): Promise<WireCompany> {
        return new WireCompany(
            (
                await this.wireCompaniesClient.wireCompaniesAccounts({
                    coreCompanyId: `${coreCompanyId}`,
                    parentCompanyId,
                    isDli: false,
                })
            ).data
        );
    }

    public async removeAchCompanies(featureId: number, companies: AchCompany[]): Promise<boolean> {
        // There isn't an endpoint for removing and array of ACH companies so we have to loop through
        // and remove them one at a time
        try {
            const promises = companies.map(company =>
                this.achClient.achCompanies(featureId, company.id)
            );
            await Promise.all(promises);
            return true;
        } catch (error) {
            throw new Error(`Failed to remove ACH companies: ${error}`);
        }
    }

    public async addAchCompanies(
        featureId: number,
        companies: AchCompany[]
    ): Promise<AchCompany[]> {
        const response = await this.achClient.achCompaniesAllPost(
            featureId,
            companies.map(company => company.toDto() as AchCompanyModelDto)
        );
        return response.data.map(achCompany => new AchCompany(achCompany));
    }

    public async getAchCompanies(companyId: number, companyName: string): Promise<AchCompany[]> {
        const response = await this.achCompaniesClient.achCompaniesSearchAll(companyId, {
            companyName: companyName,
        });
        return response.data.map(achCompany => new AchCompany(achCompany));
    }

    public async updateOffsetAccounts(
        companyId: number,
        achCompanyId: number,
        offsetAccounts: number[]
    ): Promise<CompanyAccount[]> {
        const response = await this.achClient.achOffsetAccountsAll(
            companyId,
            achCompanyId,
            offsetAccounts
        );
        return response.data.map(account => new CompanyAccount(account));
    }

    public async getAchOffsetAccounts(
        companyId: number,
        achCompanyId: number
    ): Promise<AchOffsetAccount[]> {
        const response = (await this.achClient.achOffsetAccounts(companyId, achCompanyId))
            .data as AchOffsetAccountsModelDto;
        return [
            ...response.managedAccounts.map(m => new AchOffsetAccount(m, true)),
            ...response.nonManagedAccounts.map(nm => new AchOffsetAccount(nm, false)),
        ];
    }

    public async getCompanyProductFeatures(
        companyId: number
    ): Promise<ProductFeatures.CompanyProductFeature<ProductFeatureModelDto>[]> {
        const companyIdString = companyId.toString();
        const features = (await this.companiesClient.companiesProductFeaturesAll(companyIdString))
            .data;
        return features.map(feature => new ProductFeatures.CompanyProductFeature(feature));
    }

    public async getWireCompany(
        productFeatureId: number,
        coreCompanyId: number
    ): Promise<WireCompany> {
        const { data } = await this.wiresClient.wiresCompaniesAll(productFeatureId, false);
        return new WireCompany(data.find(account => account.id === coreCompanyId));
    }

    public async getWiresAccounts(
        productFeatureId: number,
        companyId: number
    ): Promise<WireCompanyAccount[]> {
        return (
            (
                await this.wiresClient.wiresAccounts(productFeatureId, companyId, false)
            ).data.accounts?.map(a => new WireCompanyAccount(a)) ?? []
        );
    }

    public async updateWireCompanyAccounts(companyId: number, wireCompany: WireCompany) {
        return await this.wiresClient.wiresCompaniesPut(
            companyId,
            wireCompany.id,
            wireCompany.toDto()
        );
    }

    public async updateProductFeature<T extends keyof FeatureMappingType>(
        companyId: number,
        featureId: number,
        productFeature:
            | ProductFeatures.WireProductFeature
            | ProductFeatures.AchProductFeature
            | ProductFeatures.StopPaymentProductFeature
            | ProductFeatures.ReportingProductFeature
            | ProductFeatures.TransferProductFeature
            | ProductFeatures.ElectronicDocumentsProductFeature
            | ProductFeatures.WausauEStatementsProductFeature
            | ProductFeatures.BillPayProductFeature
            | ProductFeatures.RemoteDepositCaptureProductFeature
            | ProductFeatures.AccountReconProductFeature,
        featureType: T
    ): Promise<FeatureMappingType[T]> {
        if (featureType === FeatureApiType.StopPayment) {
            const stopPaymentFeature = productFeature as ProductFeatures.StopPaymentProductFeature;
            const dto: SaveCompanyStopPaymentRequestDto = {
                approvalCount: stopPaymentFeature.approvalCount,
                accountIds: stopPaymentFeature.accountIds,
                companyId,
                applicationNameType: ApplicationNameTypeStopPaymentServiceDto.BackOffice,
            };
            const response = await this.stopPaymentClient.stopPaymentSaveCompanyConfiguration(dto);
            if (response.ok) {
                return productFeature as FeatureMappingType[T];
            } else {
                throw new Error('Failed to save stop payment configuration');
            }
        }
        if (featureType === FeatureApiType.Reporting) {
            await this.saveEnableIntuitFormattedExports(
                companyId,
                (productFeature as ProductFeatures.ReportingProductFeature)
                    .enableIntuitFormattedExports
            );
        }

        if (featureType === FeatureApiType.RemoteDeposit) {
            return (await this.editRemoteDepositCaptureProductFeature(
                companyId,
                featureId,
                productFeature as ProductFeatures.RemoteDepositCaptureProductFeature
            )) as FeatureMappingType[T];
        }

        /**
         * The Wausau eStatements api put call is different from the other features,
         * the call does not return data on success,
         * so we need to handle it by checking for OK then returning the passed in object
         */
        if (featureType === FeatureApiType.Wausau) {
            const result = await this.companiesClient.companiesProductFeaturesPut(
                companyId,
                featureId,
                productFeature.toDto()
            );
            if (result.ok) {
                return productFeature as FeatureMappingType[T];
            }
        }

        /**
         * The Account Recon api put call is different from the other features,
         * the call does not return data on success,
         * so we need to handle it by checking for OK then returning the passed in object
         */
        if (featureType === FeatureApiType.AccountRecon) {
            const featureDto = (
                productFeature as unknown as ProductFeatures.AccountReconProductFeature
            ).toDto();
            const result =
                await this.accountReconClient.accountReconSaveCompanyProductConfiguration(
                    featureDto
                );
            if (result.ok) {
                return productFeature as FeatureMappingType[T];
            }
        }

        const result = await this.companiesClient.companiesProductFeaturesPut(
            companyId,
            featureId,
            productFeature.toDto()
        );

        return mapProductFeatureDto(result.data, featureType) as FeatureMappingType[T];
    }

    public async saveProductFeature<T extends keyof FeatureMappingType>(
        companyId: number,
        productFeature:
            | ProductFeatures.WireProductFeature
            | ProductFeatures.AchProductFeature
            | ProductFeatures.StopPaymentProductFeature
            | ProductFeatures.ReportingProductFeature
            | ProductFeatures.TransferProductFeature
            | ProductFeatures.ElectronicDocumentsProductFeature
            | ProductFeatures.ForeignCurrencyWireProductFeature
            | ProductFeatures.AgileticsWebEscrowProductFeature
            | ProductFeatures.WausauEStatementsProductFeature
            | ProductFeatures.BillPayProductFeature
            | ProductFeatures.RemoteDepositCaptureProductFeature,
        featureType: T
    ): Promise<FeatureMappingType[T]> {
        if (featureType === FeatureApiType.StopPayment) {
            const stopPaymentFeature = productFeature as ProductFeatures.StopPaymentProductFeature;
            const dto: SaveCompanyStopPaymentRequestDto = {
                approvalCount: stopPaymentFeature.approvalCount,
                accountIds: stopPaymentFeature.accountIds,
                companyId,
                applicationNameType: ApplicationNameTypeStopPaymentServiceDto.BackOffice,
            };
            const response = await this.stopPaymentClient.stopPaymentSaveCompanyConfiguration(dto);
            if (response.ok) {
                return productFeature as FeatureMappingType[T];
            } else {
                throw new Error('Failed to save stop payment configuration');
            }
        }
        if (featureType === FeatureApiType.Reporting) {
            await this.saveEnableIntuitFormattedExports(
                companyId,
                (productFeature as ProductFeatures.ReportingProductFeature)
                    .enableIntuitFormattedExports
            );
        }

        if (featureType === FeatureApiType.RemoteDeposit) {
            return (await this.addRemoteDepositCaptureProductFeature(
                companyId,
                productFeature as ProductFeatures.RemoteDepositCaptureProductFeature
            )) as FeatureMappingType[T];
        }

        const result = await this.companiesClient.companiesProductFeaturesPost(
            `${companyId}`,
            productFeature.toDto()
        );

        return mapProductFeatureDto(result.data, featureType) as FeatureMappingType[T];
    }

    public async getActiveCompanyAccounts(
        companyId: number,
        featureType: FeatureApiType
    ): Promise<CompanyAccount[]> {
        if (
            ![
                FeatureApiType.Transfer,
                FeatureApiType.Reporting,
                FeatureApiType.StopPayment,
                FeatureApiType.AccountRecon,
                FeatureApiType.ACH,
                FeatureApiType.Wire,
            ].includes(featureType)
        ) {
            throw new Error(`Feature type ${featureType} is not supported`);
        }
        if (featureType === FeatureApiType.StopPayment) {
            return await this.getActiveCheckingAndSavingsCompanyAccounts(companyId);
        }

        if (featureType === FeatureApiType.AccountRecon) {
            return await this.getActiveCheckingAndSavingsCompanyAccounts(companyId, false);
        }

        return await this._getActiveCompanyAccounts(companyId);
    }

    public async getPositivePayAccounts(companyId: number, type: PositivePayType) {
        const companyGuid = (
            await this.companyClient.companyGetCompanyGuidUniqueId(companyId, {
                maxAgeInSeconds: 300,
            })
        ).data;
        if (companyGuid) {
            let accountFunction: (
                companyGuid: string
            ) => Promise<
                HttpResponse<ProductFeatureAccountParticipationsResponseDto, ErrorHttpContentDto>
            >;
            switch (type) {
                default:
                case PositivePayType.Check:
                    accountFunction =
                        this.positivePayCompanyConfigurationClient
                            .positivePayCompanyConfigurationGetCheckExceptionAccounts;
                    break;
                case PositivePayType.Ach:
                    accountFunction =
                        this.positivePayCompanyConfigurationClient
                            .positivePayCompanyConfigurationGetAchExceptionAccounts;
                    break;
                case PositivePayType.AchFilters:
                    // @ts-expect-error the OpenApi annotation is not correct
                    accountFunction =
                        this.positivePayCompanyConfigurationClient
                            .positivePayCompanyConfigurationGetAchFilterManagementAccounts;
                    break;
            }
            return (
                (await accountFunction(companyGuid)).data.productFeatureAccountParticipations?.map(
                    account => new ProductFeatureAccountParticipation(account)
                ) ?? []
            );
        }
        return [];
    }

    private async getActiveCheckingAndSavingsCompanyAccounts(
        companyId: number,
        activeOnly = true
    ): Promise<CompanyAccount[]> {
        const companyIdString = companyId.toString();
        const accounts = (
            await this.companiesClient.companiesAccountsAllGet(companyIdString)
        ).data.filter(
            accountDto =>
                (activeOnly ? accountDto.status === 'Active' : true) &&
                ['Checking', 'Savings'].includes(accountDto.type ?? '')
        );

        return accounts.map(account => new CompanyAccount(account));
    }

    private async _getActiveCompanyAccounts(companyId: number): Promise<CompanyAccount[]> {
        const companyIdString = companyId.toString();
        const accounts = (
            await this.companiesClient.companiesAccountsAllGet(companyIdString)
        ).data.filter(
            accountDto =>
                accountDto.status === 'Active' &&
                !['', 'Dormant', 'Closed', 'Escheat'].includes(accountDto.account?.coreStatus ?? '')
        );

        return accounts.map(account => new CompanyAccount(account));
    }

    private async saveEnableIntuitFormattedExports(
        companyId: number,
        enableIntuitFormattedExports: boolean
    ): Promise<void> {
        await this.companiesClient.companiesSaveQfxCompanyFlag(companyId, {
            newFlag: enableIntuitFormattedExports ? 'Y' : 'N',
        });
    }

    private async editRemoteDepositCaptureProductFeature(
        companyId: number,
        productId: number,
        feature: ProductFeatures.RemoteDepositCaptureProductFeature
    ): Promise<ProductFeatures.RemoteDepositCaptureProductFeature> {
        const [remoteDepositProductTypeId] = (
            await this.companiesClient.companiesGetAvailableRemoteDepositProductTypes()
        ).data;

        const product = {
            id: productId,
            remoteDepositProductTypeId,
            ssoCustomerId: feature.customerId,
            type: 'RemoteDeposits',
        };

        const result = (
            await this.companiesClient.companiesEditRemoteDepositProduct(
                companyId,
                product as RemoteDepositProductModelDto
            )
        ).data;

        return mapProductFeatureDto(
            result,
            FeatureApiType.RemoteDeposit
        ) as ProductFeatures.RemoteDepositCaptureProductFeature;
    }

    private async addRemoteDepositCaptureProductFeature(
        companyId: number,
        feature: ProductFeatures.RemoteDepositCaptureProductFeature
    ): Promise<ProductFeatures.RemoteDepositCaptureProductFeature> {
        const [remoteDepositProductTypeId] = (
            await this.companiesClient.companiesGetAvailableRemoteDepositProductTypes()
        ).data;

        const product = {
            remoteDepositProductTypeId,
            ssoCustomerId: feature.customerId,
            type: 'RemoteDeposits',
        };
        const result = (
            await this.companiesClient.companiesAddRemoteDepositProduct(
                companyId,
                product as RemoteDepositProductModelDto
            )
        ).data;

        return mapProductFeatureDto(
            result,
            FeatureApiType.RemoteDeposit
        ) as ProductFeatures.RemoteDepositCaptureProductFeature;
    }
}
